import { DestinationParams } from '@anm/api/modules/stream';
import getDestinationsParams, { getInitialDestinationParams } from '@anm/helpers/stream/getDestinationParams';
import { getBestQuality } from '@anm/hosting-player';
import { userDestinationsActions, userDestinationsSelectors } from '@anm/store/modules/userDestinations';
import { omitBy } from 'lodash/fp';
import isEqual from 'lodash/fp/isEqual';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DestinationObject } from 'types/stream';

import { hostedVideoSelectors } from '../../../store/modules/hostedVideo';
import { streamActions, streamSelectors } from '../../../store/modules/stream';

import { getCanUpdateStreamMeta, getInitialModalDate, getStreamKind } from './helpers';
import { EditHandlerProps, SubmitProps, SubmitStreamProps } from './types';

export * from './helpers';

const useConvertRecordingToStream = (props: EditHandlerProps) => {
  const dispatch = useDispatch();
  const changeStreamKindResponse = useSelector(streamSelectors.selectChangeStreamKindResponse);

  const streamPreview = useRef('');
  const isOpenStudio = useRef(false);

  const {
    title,
    streamId,
    date,
    folderId,
    isScheduled,
    description,
    destinationIds,
    destinationParams,
    isWebinar
  } = props;

  const canUpdateLive =
    changeStreamKindResponse?.streamId === streamId &&
    changeStreamKindResponse?.kind === 'LIVE' &&
    !!changeStreamKindResponse;

  useEffect(() => {
    if (canUpdateLive && streamId) {
      const filledDestinationParams = getDestinationsParams({
        title,
        description,
        destinationIds,
        destinationParams,
        preview: streamPreview.current
      });

      streamId &&
        dispatch(
          streamActions.editStream({
            isOpenStudio: isOpenStudio.current,
            title,
            streamId,
            folderId,
            input: '',
            description,
            kind: 'LIVE',
            webinar: isWebinar,
            preview: streamPreview.current,
            schedule: isScheduled ? date : null,
            destinationParams: filledDestinationParams
          })
        );
    }
  }, [streamId, canUpdateLive]);

  const handleConvertRecordingToStream = (preview = '', canOpenStudio = false) => {
    streamPreview.current = preview;
    isOpenStudio.current = canOpenStudio;

    streamId && dispatch(streamActions.changeStreamKind({ streamId, kind: 'LIVE' }));
  };

  return handleConvertRecordingToStream;
};

const useEditLiveStream = ({
  date,
  streamId,
  videoSrc,
  projectId,
  destinations,
  isDuplicate,
  destinationIds,
  streamInitialState,
  destinationParams,
  canGuestAddDestination,
  deleteGuestDestinations,
  setTile,
  setDescription,
  setSelectedDate,
  setDestinationsParams,
  setSelectedDestinations,
  ...props
}: EditHandlerProps) => {
  const dispatch = useDispatch();
  const streamKind = getStreamKind(projectId, streamInitialState?.kind);
  const allOwnDestinations = useSelector(userDestinationsSelectors.selectDestinationsObject);

  const guestDestinations = useSelector(state => streamSelectors.selectGuestDestinations(state, streamId));

  useEffect(() => {
    if (streamInitialState) {
      const initialDestinationParams = getInitialDestinationParams(
        props.title || streamInitialState?.title,
        props.description || streamInitialState?.description,
        streamInitialState?.preview,
        destinations
      );
      const { title, description, schedule, destinationParams } = streamInitialState;

      const newDate = getInitialModalDate(schedule, isDuplicate);

      setTile(title);
      setDescription(description);
      newDate && setSelectedDate(newDate);
      setSelectedDestinations(Object.keys(destinationParams));
      initialDestinationParams && setDestinationsParams(initialDestinationParams);
    }
  }, [streamInitialState?.schedule]);

  const editStream = (preview = '', isOpenStudio = false) => {
    if (!streamId) return;

    const { folderId, title, description, isScheduled, isWebinar } = props;

    const canDeleteGuests = !!deleteGuestDestinations.length;

    const filledDestinationParams = getDestinationsParams({
      title,
      preview,
      description,
      destinationIds,
      destinationParams,
      fbGroupIds: destinations?.filter(d => d.kind === 'group' && d.network === 'FACEBOOK').map(d => d.id)
    });

    const ownDestinationParams = omitBy((_, destinationId) => !allOwnDestinations?.[destinationId])(
      filledDestinationParams
    ) as DestinationParams;

    const destinationParamsWithoutGuests = omitBy(
      (_, id) => guestDestinations?.[id] && !deleteGuestDestinations.includes(id)
    )(ownDestinationParams) as DestinationObject;

    const canUpdateStreamDestinations = !isEqual(
      omitBy((_, destinationId) => !allOwnDestinations?.[destinationId])(streamInitialState?.destinationParams || {}),
      omitBy((_, destinationId) => !allOwnDestinations?.[destinationId])(ownDestinationParams || {})
    );

    const canUpdateStreamMeta = getCanUpdateStreamMeta(
      { ...props, canGuestAddDestination, preview, schedule: date },
      streamInitialState
    );

    const canEditStream =
      canUpdateStreamDestinations || canUpdateStreamMeta || (streamInitialState?.schedule && isOpenStudio);

    const canEditSettings = streamInitialState?.canGuestAddDestination !== canGuestAddDestination;

    const editProps = {
      streamId,
      title,
      folderId,
      description,
      kind: streamKind,
      input: '',
      isOpenStudio,
      webinar: isWebinar,
      schedule: isScheduled ? date : null,
      destinationParams: destinationParamsWithoutGuests,
      preview: preview || streamInitialState?.preview || '',
      ...(projectId && { hostedVideoId: projectId })
    };

    canDeleteGuests &&
      dispatch(
        userDestinationsActions.deleteGuestDestinations({
          streamId,
          editProps,
          destinationIds: deleteGuestDestinations.toString()
        })
      );

    !canDeleteGuests && canEditStream && dispatch(streamActions.editStream(editProps));

    canEditSettings &&
      props.roomId &&
      dispatch(
        streamActions.saveCanAddGuestsRoomSettings({
          roomId: props.roomId,
          canNotify: !canEditStream && !canDeleteGuests,
          canGuestAddDestination: !!canGuestAddDestination
        })
      );
  };

  return editStream;
};

const useCreateStream = ({
  date,
  title,
  isWebinar,
  description,
  videoSrc,
  projectId = '',
  folderId,
  roomId,
  isDuplicate,
  isScheduled,
  destinationIds,
  destinations,
  destinationParams,
  canGuestAddDestination
}: SubmitStreamProps) => {
  const dispatch = useDispatch();
  const videoUrl = useVideoUrl(videoSrc);
  const streamKind = getStreamKind(projectId);

  const streamInput = projectId ? videoUrl : '';

  const createStream = (preview = '', isOpenStudio = false) => {
    const filledDestinationParams = getDestinationsParams({
      title,
      preview,
      description,
      destinationIds,
      destinationParams,
      fbGroupIds: destinations?.filter(d => d.kind === 'group' && d.network === 'FACEBOOK').map(d => d.id)
    });

    const action = isDuplicate ? streamActions.duplicateStream : streamActions.createStream;

    dispatch(
      action({
        title,
        preview,
        folderId,
        description,
        canGuestAddDestination,
        kind: streamKind,
        isOpenStudio,
        input: streamInput,
        webinar: isWebinar,
        schedule: isScheduled ? date : null,
        destinationParams: filledDestinationParams,
        ...(projectId && { hostedVideoId: projectId }),
        ...(isDuplicate && { roomId })
      })
    );
  };

  return createStream;
};

export const useVideoUrl = (videoSrc?: string) => {
  const videoUrl = videoSrc || useSelector(hostedVideoSelectors.selectVideoUrl);

  if (videoUrl) return videoUrl;

  const manifest = useSelector(hostedVideoSelectors.selectManifest);
  const videoFiles = manifest?.main.filter(file => file.type === 'video');
  const bestQualityVideoSrc = videoFiles && getBestQuality(videoFiles)?.path;

  return bestQualityVideoSrc || '';
};

export const useIsSubmitPending = () => {
  const isCreateStreamPending = useSelector(streamSelectors.selectCreateStreamPending);
  const isEditStreamPending = useSelector(streamSelectors.selectEditStreamIsPending);
  const isUploadingPending = useSelector(streamSelectors.selectIsPendingEditDestinationMeta);
  const isChangeKindPending = useSelector(streamSelectors.selectChangeStreamKindIsPending);
  const isPendingDuplicate = useSelector(streamSelectors.selectIsPendingDuplicateStream);

  const isDeleteGuestsPending = useSelector(userDestinationsSelectors.selectDeleteGuestsIsPending);

  const isSubmitButtonPending =
    isCreateStreamPending || isEditStreamPending || isChangeKindPending || isPendingDuplicate || isDeleteGuestsPending;

  return [isSubmitButtonPending, isUploadingPending] as const;
};

const useSubmitStream = ({
  isEdit,
  setTile,
  setDescription,
  setSelectedDate,
  setSelectedDestinations,
  streamInitialState,
  ...props
}: SubmitProps) => {
  const createStream = useCreateStream(props);

  const isRecording = streamInitialState?.kind === 'RECORD';

  const editProps = {
    ...props,
    setTile,
    setDescription,
    setSelectedDate,
    setSelectedDestinations,
    streamInitialState
  };

  const editLiveStream = useEditLiveStream(editProps);
  const convertRecordingToStream = useConvertRecordingToStream(editProps);

  const editStream = isRecording ? convertRecordingToStream : editLiveStream;

  const onSubmit = isEdit && !props.isDuplicate ? editStream : createStream;

  return onSubmit;
};

export default useSubmitStream;
